import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Users } from 'lucide-react';
import { ClearableNumberInput } from '../../ui/clearable-number-input';

/**
 * Shared PassengerDetails component for Quotation Wizard
 * Used by: PredefinedRoute, CustomRoute, and CustomPackageBuilder
 */
const PassengerDetails = ({
  noAdults,
  noChildrenWithBed,
  noChildrenNoBed,
  noInfants,
  onAdultsChange,
  onChildrenWithBedChange,
  onChildrenNoBedChange,
  onInfantsChange,
  showSummary = true,
  suggestedSingleRooms = 0,
  suggestedDoubleRooms = 1,
  compact = false,
}) => {
  const totalPax = (noAdults || 0) + (noChildrenWithBed || 0) + (noChildrenNoBed || 0) + (noInfants || 0);

  if (compact) {
    // Compact version without card wrapper
    return (
      <div className="space-y-4">
        {/* Mobile: 2-col, Desktop: 4-col per spec */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Adults</Label>
            <ClearableNumberInput
              min={1}
              max={50}
              value={noAdults || 1}
              onChange={onAdultsChange}
              data-testid="pax-adults-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Children (Bed)</Label>
            <ClearableNumberInput
              min={0}
              max={50}
              value={noChildrenWithBed || 0}
              onChange={onChildrenWithBedChange}
              data-testid="pax-children-bed-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Children (No Bed)</Label>
            <ClearableNumberInput
              min={0}
              max={50}
              value={noChildrenNoBed || 0}
              onChange={onChildrenNoBedChange}
              data-testid="pax-children-nobed-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Infants</Label>
            <ClearableNumberInput
              min={0}
              max={50}
              value={noInfants || 0}
              onChange={onInfantsChange}
              data-testid="pax-infants-input"
            />
          </div>
        </div>
        {showSummary && (
          <p className="text-xs text-[var(--g500)] mt-2">
            Total: {totalPax} passengers
          </p>
        )}
      </div>
    );
  }

  return (
    <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]" data-testid="passenger-details-card">
      <CardHeader className="py-3 px-4 sm:px-6 bg-white border-b border-[var(--g200)]">
        <CardTitle className="text-sm font-bold text-[var(--navy)] flex items-center gap-2">
          <Users className="w-4 h-4 text-[var(--navy)]" />
          Passenger Details
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        {/* Mobile: 2-col, Desktop: 4-col per spec */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Adults <span className="text-[var(--err)]">*</span></Label>
            <ClearableNumberInput
              min={1}
              max={50}
              value={noAdults || 2}
              onChange={onAdultsChange}
              data-testid="adults-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Children (Bed)</Label>
            <ClearableNumberInput
              min={0}
              max={50}
              value={noChildrenWithBed || 0}
              onChange={onChildrenWithBedChange}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Children (No Bed)</Label>
            <ClearableNumberInput
              min={0}
              max={50}
              value={noChildrenNoBed || 0}
              onChange={onChildrenNoBedChange}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Infants</Label>
            <ClearableNumberInput
              min={0}
              max={50}
              value={noInfants || 0}
              onChange={onInfantsChange}
            />
          </div>
        </div>
        {showSummary && (
          <p className="text-xs text-[var(--g500)] mt-4">
            Total: {totalPax} pax · Suggested: {suggestedDoubleRooms} Double{suggestedSingleRooms > 0 ? `, ${suggestedSingleRooms} Single` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PassengerDetails;
